/**
 * Local Print Service
 *
 * Handles direct printing to the desktop billing app via localhost:9100.
 * This bypasses the Socket.IO path and prints directly when the desktop app is running.
 *
 * IMPORTANT: The data format must match the backend's buildReceiptData() method in
 * InvoiceController.java to ensure consistency between localhost and Socket.IO print paths.
 *
 * @see backend/src/main/java/com/sog/backend/controller/InvoiceController.java#buildReceiptData
 */

const LOCAL_PRINT_URL = 'http://localhost:9100';
const TIMEOUT_MS = 3000;

export interface LocalPrintResult {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  invoiceNumber?: string;
  kotNumber?: string;
}

export interface LocalPrinterStatus {
  success: boolean;
  server?: {
    running: boolean;
    port: number;
    url: string;
  };
  printer?: {
    configured: boolean;
    type: string;
    address: string;
    displayName: string;
    paperWidth: number;
    autoCut: boolean;
  };
  autoPrint?: {
    enabled: boolean;
    initialized: boolean;
  };
  kot?: {
    todayCount: number;
    printerConfigured: boolean;
  };
  socketIO?: {
    connected: boolean;
  };
  timestamp?: string;
}

/**
 * KOT (Kitchen Order Ticket) data structure
 * This format is used for both HTTP endpoint and Socket.IO events
 */
export interface KOTData {
  kotNumber: string;
  id?: number;
  orderId: number;
  orderNumber?: string;
  tableNumber?: string;
  floorName?: string;
  sectionName?: string;
  customerName?: string;
  kitchenId?: number;
  kitchenName?: string;
  orderType?: string;
  specialInstructions?: string;
  priority?: string;
  status?: string;
  items: Array<{
    itemName: string;
    quantity: number;
    portion?: string;
    specialInstructions?: string;
  }>;
  createdAt?: string;
}

// Invoice data interface matching InvoiceModal's Invoice type
export interface InvoiceData {
  id: number;
  invoiceNumber?: string;
  createdAt: string;
  total: number;
  subtotal?: number;
  order?: {
    id: number;
    customOrderId?: string;
    qrCode?: { code: string; location?: string };
    user?: { id: number; username: string; email?: string; phone?: string };
    guestName?: string;
    guestPhone?: string;
    customerName?: string;
    items?: Array<{
      id: number;
      menuItem?: { id: number; name: string; price: number };
      menuItemName?: string;
      quantity: number;
      price: number;
      portion?: string;
      comment?: string;
      selectedToppings?: Array<{ id: number; name: string; price: number }>;
      toppings?: Array<{ id: number; name: string; price?: number }>;
      freeToppingsCount?: number;
      requestedSpiceLevel?: string;
    }>;
    deliveryType?: string;
    orderType?: string;
  };
  vendor?: {
    id: number;
    username: string;
    businessName?: string;
    address?: string;
    phone?: string;
    email?: string;
    taxId?: string;
    gstin?: string;
    vatNumber?: string;
  };
  items?: Array<{
    id: number;
    menuItem?: { id: number; name: string; price: number };
    menuItemName?: string;
    quantity: number;
    price: number;
    portion?: string;
    comment?: string;
    selectedToppings?: Array<{ id: number; name: string; price: number }>;
    toppings?: Array<{ id: number; name: string; price?: number }>;
    freeToppingsCount?: number;
    requestedSpiceLevel?: string;
  }>;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  couponCode?: string;
  discountReason?: string;
  /** Authoritative totals from the backend read-back (InvoiceCalculationService). */
  grandTotal?: number;
  roundOff?: number;
  /** GB VAT (tax-inclusive) fields — absent on the GST path */
  vatRate?: number;
  vatAmount?: number;
  currencyCode?: string;
  paymentMethod?: string;
  paymentNotes?: string;
  isConsolidated?: boolean;
  isPreview?: boolean;
  consolidatedOrders?: Array<{
    id: number;
    customOrderId?: string;
    items?: Array<{
      id: number;
      menuItem?: { id: number; name: string; price: number };
      menuItemName?: string;
      quantity: number;
      price: number;
      portion?: string;
      comment?: string;
      selectedToppings?: Array<{ id: number; name: string; price: number }>;
      toppings?: Array<{ id: number; name: string; price?: number }>;
      freeToppingsCount?: number;
      requestedSpiceLevel?: string;
    }>;
  }>;
}

/**
 * Receipt data format matching backend's buildReceiptData() in InvoiceController.java
 * This is the SINGLE SOURCE OF TRUTH for print data format.
 *
 * @see backend/src/main/java/com/sog/backend/controller/InvoiceController.java:973
 */
interface ReceiptData {
  // Basic invoice info
  invoiceId: number | null;
  invoiceNumber: string;
  orderId: number;
  orderNumber: string;
  createdAt: string;
  isPreview?: boolean;

  // Table info (with floor and section)
  tableName: string;
  tableId?: number | null;
  floorName?: string | null;
  sectionName?: string | null;

  // Customer info
  customerName: string;
  phoneNumber?: string | null;

  // Order type
  orderType: string;

  // Items - matches backend ItemData structure
  items: Array<{
    name: string;
    quantity: number;
    price: number;
    total: number;
    portion?: string | null;
    spiceLevel?: string | null;
    notes?: string | null;
    orderNumber?: string; // For consolidated invoices - tracks which order item came from
  }>;
  isConsolidated: boolean;

  // Totals and taxes
  subtotal: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  serviceChargeRate: number;
  serviceChargeAmount: number;

  // Discount details
  discountAmount: number;
  discountType?: string | null;
  discountValue?: number | null;
  couponCode?: string | null;
  discountReason?: string | null;

  // Final totals
  total: number; // Total before discount (subtotal + taxes)
  roundOff: number;
  grandTotal: number; // Final amount after discount and round off

  // Tax breakdown as list for receipt rendering
  taxes: Array<{
    name: string;
    rate: number;
    amount: number;
  }>;

  // Payment info
  paymentMethod?: string | null;
  cashierName?: string | null;

  // GB VAT (tax-inclusive) fields — null on the GST path. VAT is contained
  // inside the gross amounts, never added on top of grandTotal.
  vatRate?: number | null;
  vatAmount?: number | null;
  currencyCode?: string;

  // Vendor info - flattened for Dart compatibility
  vendorName: string;
  vendorGstin: string;
  vendorVatNumber?: string;
  vendorAddress: string;
  vendorPhone: string;

  // Timestamp
  timestamp?: number;
}

/**
 * Check if the local print server is available
 */
export async function checkLocalPrinterAvailable(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const response = await fetch(`${LOCAL_PRINT_URL}/health`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get detailed status of the local print server and printer
 */
export async function getLocalPrinterStatus(): Promise<LocalPrinterStatus | null> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const response = await fetch(`${LOCAL_PRINT_URL}/status`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Transform invoice data from webapp format to receipt data format.
 * This matches the backend's buildReceiptData() method in InvoiceController.java
 *
 * @see backend/src/main/java/com/sog/backend/controller/InvoiceController.java:973
 */
function transformInvoiceData(invoice: InvoiceData): ReceiptData {
  const order = invoice.order;

  // Build items array - handle consolidated and single orders
  const items: ReceiptData['items'] = [];
  const isConsolidated = invoice.isConsolidated || false;

  if (isConsolidated && invoice.consolidatedOrders) {
    // Consolidated invoice - collect items from all orders
    for (const consolidatedOrder of invoice.consolidatedOrders) {
      if (consolidatedOrder.items) {
        for (const item of consolidatedOrder.items) {
          items.push({
            name: item.menuItemName || item.menuItem?.name || 'Unknown Item',
            quantity: item.quantity,
            price: item.price,
            total: item.price * item.quantity,
            portion: item.portion || null,
            spiceLevel: item.requestedSpiceLevel && item.requestedSpiceLevel !== 'NONE'
              ? item.requestedSpiceLevel : null,
            notes: item.comment || null,
            orderNumber: consolidatedOrder.customOrderId, // Track which order item came from
          });
        }
      }
    }
  } else {
    // Single order - use invoice items or order items
    const sourceItems = invoice.items || order?.items || [];
    for (const item of sourceItems) {
      items.push({
        name: item.menuItemName || item.menuItem?.name || 'Unknown Item',
        quantity: item.quantity,
        price: item.price,
        total: item.price * item.quantity,
        portion: item.portion || null,
        spiceLevel: item.requestedSpiceLevel && item.requestedSpiceLevel !== 'NONE'
          ? item.requestedSpiceLevel : null,
        notes: item.comment || null,
      });
    }
  }

  // Get tax values with defaults
  const subtotal = invoice.subtotal || 0;
  const cgstRate = invoice.cgstRate || 0;
  const cgstAmount = invoice.cgstAmount || 0;
  const sgstRate = invoice.sgstRate || 0;
  const sgstAmount = invoice.sgstAmount || 0;
  const igstRate = invoice.igstRate || 0;
  const igstAmount = invoice.igstAmount || 0;
  const serviceChargeRate = invoice.serviceChargeRate || 0;
  const serviceChargeAmount = invoice.serviceChargeAmount || 0;
  const discountAmount = invoice.discountAmount || 0;

  // Calculate total (subtotal + taxes, before discount)
  const total = subtotal + cgstAmount + sgstAmount + igstAmount + serviceChargeAmount;

  // Totals are display-only here (UK plan Phase 2 step 0): prefer the
  // backend's authoritative grandTotal/roundOff — the saved-invoice read-back
  // now derives them via InvoiceCalculationService. Recompute only as a
  // legacy fallback for payloads that predate those fields.
  const totalAfterDiscount = total - discountAmount;
  const grandTotal = typeof invoice.grandTotal === 'number'
    ? invoice.grandTotal
    : Math.round(totalAfterDiscount);
  const roundOff = typeof invoice.roundOff === 'number'
    ? invoice.roundOff
    : Math.round((grandTotal - totalAfterDiscount) * 100) / 100;

  // Build taxes array for receipt rendering
  const taxes: ReceiptData['taxes'] = [];
  if (cgstRate > 0 && cgstAmount > 0) {
    taxes.push({ name: 'CGST', rate: cgstRate, amount: cgstAmount });
  }
  if (sgstRate > 0 && sgstAmount > 0) {
    taxes.push({ name: 'SGST', rate: sgstRate, amount: sgstAmount });
  }
  if (igstRate > 0 && igstAmount > 0) {
    taxes.push({ name: 'IGST', rate: igstRate, amount: igstAmount });
  }
  if (serviceChargeRate > 0 && serviceChargeAmount > 0) {
    taxes.push({ name: 'Service Charge', rate: serviceChargeRate, amount: serviceChargeAmount });
  }
  // GB tax-inclusive VAT: labelled as included so agents that render the taxes
  // array generically don't imply it was added on top of the total.
  const vatRate = typeof invoice.vatRate === 'number' ? invoice.vatRate : null;
  const vatAmount = typeof invoice.vatAmount === 'number' ? invoice.vatAmount : null;
  if (vatRate !== null && vatAmount !== null && vatAmount > 0) {
    taxes.push({ name: 'Includes VAT', rate: vatRate, amount: vatAmount });
  }

  // Customer info
  let customerName = 'Guest';
  let phoneNumber: string | null = null;
  if (order?.user) {
    customerName = order.user.username;
    phoneNumber = order.user.phone || null;
  } else if (order?.guestName) {
    customerName = order.guestName;
    phoneNumber = order.guestPhone || null;
  } else if (order?.customerName) {
    customerName = order.customerName;
  }

  // Build receipt data matching backend format
  const receiptData: ReceiptData = {
    // Basic invoice info
    invoiceId: invoice.id > 0 ? invoice.id : null,
    invoiceNumber: invoice.invoiceNumber || (invoice.id > 0 ? invoice.id.toString() : `PREVIEW-${order?.id || 0}`),
    orderId: order?.id || 0,
    orderNumber: order?.customOrderId || '',
    createdAt: invoice.createdAt || new Date().toISOString(),
    isPreview: invoice.isPreview || invoice.id <= 0,

    // Table info
    tableName: order?.qrCode?.location || order?.qrCode?.code || 'N/A',
    tableId: null, // Not available in frontend invoice data
    floorName: null, // Not available in frontend invoice data
    sectionName: null, // Not available in frontend invoice data

    // Customer info
    customerName,
    phoneNumber,

    // Order type
    orderType: order?.orderType || order?.deliveryType || 'DINE_IN',

    // Items
    items,
    isConsolidated,

    // Totals and taxes
    subtotal: Math.round(subtotal * 100) / 100,
    cgstRate,
    cgstAmount: Math.round(cgstAmount * 100) / 100,
    sgstRate,
    sgstAmount: Math.round(sgstAmount * 100) / 100,
    igstRate,
    igstAmount: Math.round(igstAmount * 100) / 100,
    serviceChargeRate,
    serviceChargeAmount: Math.round(serviceChargeAmount * 100) / 100,

    // Discount details
    discountAmount: Math.round(discountAmount * 100) / 100,
    discountType: invoice.discountType || null,
    discountValue: invoice.discountValue || null,
    couponCode: invoice.couponCode || null,
    discountReason: invoice.discountReason || null,

    // Final totals
    total: Math.round(total * 100) / 100,
    roundOff,
    grandTotal,

    // Tax breakdown
    taxes,

    // Payment info
    paymentMethod: invoice.paymentMethod || null,
    cashierName: null, // Not available in frontend

    // GB VAT fields (null on the GST path)
    vatRate,
    vatAmount,
    currencyCode: invoice.currencyCode,

    // Vendor info - flattened for Dart compatibility
    vendorName: invoice.vendor?.businessName || invoice.vendor?.username || '',
    vendorGstin: invoice.vendor?.gstin || invoice.vendor?.taxId || '',
    vendorVatNumber: invoice.vendor?.vatNumber || '',
    vendorAddress: invoice.vendor?.address || '',
    vendorPhone: invoice.vendor?.phone || '',

    // Timestamp
    timestamp: Date.now(),
  };

  return receiptData;
}

/**
 * Print to local printer via localhost:9100
 * @returns Result object with success status and optional error message
 */
export async function printToLocalPrinter(invoiceData: InvoiceData): Promise<LocalPrintResult> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const receiptData = transformInvoiceData(invoiceData);

    console.log('[LocalPrint] Sending print request to localhost:9100', {
      invoiceNumber: receiptData.invoiceNumber,
      orderNumber: receiptData.orderNumber,
      itemCount: receiptData.items.length,
      isConsolidated: receiptData.isConsolidated,
      grandTotal: receiptData.grandTotal,
    });

    const response = await fetch(`${LOCAL_PRINT_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(receiptData),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const result: LocalPrintResult = await response.json();

    if (result.success) {
      console.log('[LocalPrint] Print successful');
    } else {
      console.log('[LocalPrint] Print failed:', result.error);
    }

    return result;
  } catch (error) {
    const errorMessage =
      error instanceof Error && error.name === 'AbortError'
        ? 'Connection timeout - desktop app may not be running'
        : 'Local printer not available';

    console.log('[LocalPrint] Error:', errorMessage);

    return {
      success: false,
      error: errorMessage,
      code: 'CONNECTION_FAILED',
    };
  }
}

/**
 * Print KOT (Kitchen Order Ticket) to local printer via localhost:9100
 *
 * This sends the KOT data directly to the desktop app's /print-kot endpoint.
 * The desktop app handles deduplication for manual prints (bypasses dedup check).
 *
 * @param kotData - KOT data matching the unified KOTData format
 * @returns Result object with success status and optional error message
 *
 * @example
 * ```typescript
 * const result = await printKOT({
 *   kotNumber: 'KOT-20250130-0001',
 *   orderId: 456,
 *   orderNumber: 'ORD-2025-0456',
 *   tableNumber: 'T5',
 *   kitchenName: 'Main Kitchen',
 *   items: [{ itemName: 'Chicken Biryani', quantity: 2, portion: 'FULL' }]
 * });
 *
 * if (!result.success) {
 *   console.error('KOT print failed:', result.error);
 * }
 * ```
 */
export async function printKOT(kotData: KOTData): Promise<LocalPrintResult> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    console.log('[LocalPrint] Sending KOT print request to localhost:9100', {
      kotNumber: kotData.kotNumber,
      orderId: kotData.orderId,
      orderNumber: kotData.orderNumber,
      kitchenName: kotData.kitchenName,
      itemCount: kotData.items.length,
    });

    const response = await fetch(`${LOCAL_PRINT_URL}/print-kot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(kotData),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const result: LocalPrintResult = await response.json();

    if (result.success) {
      console.log('[LocalPrint] KOT print successful:', kotData.kotNumber);
    } else {
      console.log('[LocalPrint] KOT print failed:', result.error);
    }

    return result;
  } catch (error) {
    const errorMessage =
      error instanceof Error && error.name === 'AbortError'
        ? 'Connection timeout - desktop app may not be running'
        : 'Local printer not available';

    console.log('[LocalPrint] KOT Error:', errorMessage);

    return {
      success: false,
      error: errorMessage,
      code: 'CONNECTION_FAILED',
      kotNumber: kotData.kotNumber,
    };
  }
}

/**
 * Print a test KOT to verify printer configuration
 * @returns Result object with success status
 */
export async function printTestKOT(): Promise<LocalPrintResult> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    console.log('[LocalPrint] Sending test KOT print request');

    const response = await fetch(`${LOCAL_PRINT_URL}/test-kot-print`, {
      method: 'POST',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const result: LocalPrintResult = await response.json();

    if (result.success) {
      console.log('[LocalPrint] Test KOT print successful');
    } else {
      console.log('[LocalPrint] Test KOT print failed:', result.error);
    }

    return result;
  } catch (error) {
    const errorMessage =
      error instanceof Error && error.name === 'AbortError'
        ? 'Connection timeout - desktop app may not be running'
        : 'Local printer not available';

    console.log('[LocalPrint] Test KOT Error:', errorMessage);

    return {
      success: false,
      error: errorMessage,
      code: 'CONNECTION_FAILED',
    };
  }
}
